import { FC, useEffect, useState } from "react";
import ModalComponent, {
  ModalProps,
} from "../../../../../../common/components/modal/modal.component";
import Row from "../../../../../../common/components/grid/row";
import Column from "../../../../../../common/components/grid/column";
import DriverDetailsCandidatureManagementRouteListingComponent from "./route-listing/driver-details-candidature-management-route-listing.component";
import ButtonComponent from "../../../../../../common/components/button/button.component";
import notificationService from "../../../../../../common/utils/notification/notification.service";
import userTranslationsHelper from "../../../../../../languages/user-translations.helper";
import DriverDetailsCandidatureManagementPlanEntry from "./common/types/driver-details-candidature-management-plan-entry";
import driverDetailsCandidatureManagementPlanEntriesAfterCandidatureApproveFactory from "./common/driver-details-candidature-management-plan-entries-after-candidature-approve.factory";
import { useAppContext } from "../../../../../../context/app.context";
import driverDetailsUserPermissionsHelper from "../../common/user-permissions/driver-details-user-permission.helper";
import LoaderComponent from "../../../../../../common/components/loader/loader.component";
import DriverDetailsDriverPlanEntry from "../../active-route-plans/common/types/driver-details-plan-entry";
import NoticeBoxComponent from "../../../../../../common/components/notice-box/notice-box.component";
import NoticeBoxType from "../../../../../../common/components/notice-box/notice-box-type";
import useAbort from "../../../../../../common/hooks/use-abort";
import useDriverPotentialPlan from "../../../../../../common/services/driver/potential-plan/use-driver-potential-plan";
import DriverPotentialPlanParams from "../../../../../../common/services/driver/potential-plan/driver-potential-plan-params";
import { DriverPotentialPlanEntry } from "../../../../../../common/services/driver/potential-plan/driver-potential-plan";
import driverService from "../../../../../../common/services/driver/driver.service";
import DriverPlanAcceptParams from "../../../../../../common/services/driver/plan/accept/driver-plan-accept-params";
import DriverPlanAcceptError from "../../../../../../common/services/driver/plan/accept/driver-plan-accept-error";
import DriverCandidatureDeleteParams from "../../../../../../common/services/driver/candidature/delete/driver-candidature-delete-params";

type DriverDetailsCandidatureManagementProps = Pick<
  ModalProps,
  "isOpen" | "onClose"
> & {
  driverUuid: string;
  onAcceptCandidatureSuccess: () => void;
  onDeleteCandidatureSuccess: () => void;
  currentPlanEntries: DriverDetailsDriverPlanEntry[];
};

const DriverDetailsCandidatureManagementComponent: FC<
  DriverDetailsCandidatureManagementProps
> = (props) => {
  const translations =
    userTranslationsHelper.getDriverDetailsCandidatureTranslations().management;

  const { user } = useAppContext();

  const userPermissions = driverDetailsUserPermissionsHelper.getPermissions(
    user?.roles!,
  );

  const [isAcceptCandidatureFetching, setIsAcceptCandidatureFetching] =
    useState(false);
  const [isDeleteCandidatureFetching, setIsDeleteCandidatureFetching] =
    useState(false);

  const driverPotentialPlan = useDriverPotentialPlan();
  const driverPotentialPlanAbort = useAbort();

  const acceptPlanCandidatureAbort = useAbort();

  const deletePlanCandidatureAbort = useAbort();

  const [
    planEntriesAfterCandidatureAccept,
    setPlanEntriesAfterCandidatureAccept,
  ] = useState<DriverDetailsCandidatureManagementPlanEntry[]>([]);

  const onAcceptCandidatureSuccess = () => {
    notificationService.success(
      translations.successCandidatureAcceptanceNotificationText,
    );
    props.onAcceptCandidatureSuccess();
  };

  const onAcceptCandidatureFailure = (error: DriverPlanAcceptError) => {
    notificationService.error(error.message);
  };

  const acceptCandidature = () => {
    if (!props.driverUuid) {
      return;
    }

    setIsAcceptCandidatureFetching(true);

    const params: DriverPlanAcceptParams = {
      driverUuid: props.driverUuid,
    };

    driverService
      .acceptPlanCandidature(params, acceptPlanCandidatureAbort.signal)
      .then(onAcceptCandidatureSuccess)
      .catch(onAcceptCandidatureFailure)
      .finally(() => setIsAcceptCandidatureFetching(false));
  };

  const onAcceptCandidatureButtonClick = () => {
    acceptCandidature();
  };

  const onDeleteCandidatureSuccess = () => {
    notificationService.success(
      translations.successCandidatureDeletionNotificationText,
    );
    props.onDeleteCandidatureSuccess();
  };

  const onDeleteCandidatureFailure = () => {
    notificationService.error(
      translations.failureCandidatureDeletionNotificationText,
    );
  };

  const deleteCandidature = () => {
    if (!props.driverUuid) {
      return;
    }

    setIsDeleteCandidatureFetching(true);

    const params: DriverCandidatureDeleteParams = {
      driverUuid: props.driverUuid,
    };

    driverService
      .deletePlanCandidature(params, deletePlanCandidatureAbort.signal)
      .then(onDeleteCandidatureSuccess)
      .catch(onDeleteCandidatureFailure)
      .finally(() => setIsDeleteCandidatureFetching(false));
  };

  const onDeleteCandidatureButtonClick = () => {
    deleteCandidature();
  };

  const onRejectButtonClick = () => {
    props.onClose();
  };

  const onDriverRoutePlansAfterCandidatureFetchSuccess = (
    responsePlanEntries: DriverPotentialPlanEntry[],
  ) => {
    const planEntriesAfterCandidatureAccept =
      driverDetailsCandidatureManagementPlanEntriesAfterCandidatureApproveFactory.createPlanEntries(
        responsePlanEntries,
      );
    setPlanEntriesAfterCandidatureAccept(planEntriesAfterCandidatureAccept);
  };

  const fetchDriverRoutePlansAfterCandidatureApproval = () => {
    if (!props.driverUuid) {
      return;
    }

    const params: DriverPotentialPlanParams = {
      driverUuid: props.driverUuid,
    };

    driverPotentialPlan.load(params, driverPotentialPlanAbort.signal);
  };

  useEffect(() => {
    if (!driverPotentialPlan.data.entries) {
      return;
    }
    onDriverRoutePlansAfterCandidatureFetchSuccess(
      driverPotentialPlan.data.entries,
    );
  }, [driverPotentialPlan.data.entries]);

  useEffect(() => {
    if (!props.isOpen) {
      return;
    }

    fetchDriverRoutePlansAfterCandidatureApproval();
  }, [props.isOpen]);

  const ErrorContent = (
    <NoticeBoxComponent
      type={NoticeBoxType.DANGER}
      classNames={{ root: "w-100" }}
    >
      {translations.failureCandidatureFetchNotificationText}
    </NoticeBoxComponent>
  );

  const Content = (
    <>
      <div className="mb-4">
        {translations.planAfterApprovalOfCandidatureHeadingText}
      </div>
      {planEntriesAfterCandidatureAccept.map((route) => {
        const currentPlanEntryWaypoints =
          props.currentPlanEntries.find((entry) => entry.uuid === route.uuid)
            ?.waypoints ?? [];
        return (
          <div key={route.uuid} className="mb-4">
            <div>
              {route.id
                ? translations.planAfterApprovalOfCandidatureRouteIdTemplateText.replace(
                    "#{routeId}",
                    String(route.id),
                  )
                : translations.planAfterApprovalOfCandidatureNewRouteText}
            </div>
            <DriverDetailsCandidatureManagementRouteListingComponent
              routeWaypoints={route.waypoints}
              currentWaypoints={currentPlanEntryWaypoints}
            />
          </div>
        );
      })}
    </>
  );

  const isAcceptButtonVisible =
    userPermissions.hasAccessToAcceptCandidature &&
    !driverPotentialPlan.isError &&
    !driverPotentialPlan.isLoading;

  const isDeleteButtonVisible =
    userPermissions.hasAccessToDeleteCandidature &&
    !driverPotentialPlan.isError &&
    !driverPotentialPlan.isLoading;

  return (
    <ModalComponent
      isOpen={props.isOpen}
      onClose={props.onClose}
      header={{ title: translations.headingText }}
      classNames={{
        content: "driver_details_candidature_route_listing w-100",
      }}
      actions={[
        isAcceptButtonVisible && (
          <ButtonComponent
            onClick={onAcceptCandidatureButtonClick}
            title={translations.acceptCandidatureButtonTitle}
            type="success"
            isLoading={isAcceptCandidatureFetching}
          >
            {translations.acceptCandidatureButtonText}
          </ButtonComponent>
        ),
        isDeleteButtonVisible && (
          <ButtonComponent
            onClick={onDeleteCandidatureButtonClick}
            title={translations.deleteCandidatureButtonTitle}
            isLoading={isDeleteCandidatureFetching}
            type="danger"
          >
            {translations.deleteCandidatureButtonText}
          </ButtonComponent>
        ),
        <ButtonComponent
          onClick={onRejectButtonClick}
          title={translations.rejectButtonTitle}
          type="brand"
        >
          {translations.rejectButtonText}
        </ButtonComponent>,
      ]}
    >
      <Row>
        <Column>
          {driverPotentialPlan.isLoading ? (
            <div className="w-100">
              <LoaderComponent type="primary" />
            </div>
          ) : driverPotentialPlan.isError ? (
            ErrorContent
          ) : (
            Content
          )}
        </Column>
      </Row>
    </ModalComponent>
  );
};

export default DriverDetailsCandidatureManagementComponent;
